import React from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import "../../admin-design.css";
import { logout } from "../../auth";

// ── SVG icon set (Lucide-style hand-drawn paths) ─────────────────────
function Icon({ name, size = 20 }) {
  const paths = {
    home:      ["M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z", "M9 22V12h6v10"],
    zap:       ["M13 2L4 14h7l-1 8 9-12h-7z"],
    list:      ["M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"],
    users:     ["M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2", "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z", "M23 21v-2a4 4 0 0 0-3-3.87", "M16 3.13a4 4 0 0 1 0 7.75"],
    plus:      ["M12 5v14M5 12h14"],
    monitor:   ["M2 3h20a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z", "M8 21l4-4 4 4"],
    film:      ["M2 3h20v18H2z", "M7 3v18M17 3v18M2 12h20M2 7.5h5M17 7.5h5M2 16.5h5M17 16.5h5"],
    megaphone: ["M3 11l19-5v12L3 14v-3z", "M11.6 16.8a3 3 0 1 1-5.8-1.6"],
    doc:       ["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z", "M14 2v6h6", "M16 13H8M16 17H8M10 9H8"],
    shield:    ["M12 2 4 5v6c0 5 3.5 8 8 11 4.5-3 8-6 8-11V5l-8-3z", "M9.5 12.5l1.8 1.8 3.7-4.3"],
    logout:    ["M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4", "M16 17l5-5-5-5M21 12H9"],
  };
  const d = paths[name] || paths.doc;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      {d.map((p, i) => <path key={i} d={p} />)}
    </svg>
  );
}

// ── Sidebar items ────────────────────────────────────────────────────
const ITEMS = [
  { to: "/admin/dashboard",       icon: "home",      label: "Dashboard",         end: true },
  { to: "/admin/ativacoes",       icon: "zap",       label: "Ativações",         end: true },
  { to: "/admin/ativacoes/lista", icon: "list",      label: "Listar Ativações"               },
  { to: "/admin/partners",        icon: "users",     label: "Parceiros",         end: true  },
  { to: "/admin/partners/new",    icon: "plus",      label: "Cadastrar Parceiro"             },
  { to: "/admin/screens",         icon: "monitor",   label: "Telas Indoor"                   },
  { to: "/admin/media-assets",    icon: "film",      label: "Mídias Indoor"                  },
  { to: "/admin/campaigns",       icon: "megaphone", label: "Campanhas"                      },
  { to: "/admin/condo-notices",   icon: "doc",       label: "Comunicados"                    },
  { to: "/admin/condo-users",     icon: "users",     label: "Usuários do Condomínio"         },
  { to: "/admin/auth-audit-logs", icon: "shield",    label: "Auditoria de Login"             },
];

// Map path → breadcrumb label
const BREADCRUMBS = {
  "/admin/dashboard":       "Dashboard",
  "/admin/ativacoes":       "Ativações",
  "/admin/ativacoes/lista": "Listar Ativações",
  "/admin/partners":        "Parceiros",
  "/admin/partners/new":    "Cadastrar Parceiro",
  "/admin/screens":         "Telas Indoor",
  "/admin/media-assets":    "Mídias Indoor",
  "/admin/campaigns":       "Campanhas",
  "/admin/condo-notices":   "Comunicados",
  "/admin/condo-users":     "Usuários do Condomínio",
  "/admin/auth-audit-logs": "Auditoria de Login",
};

export default function AdminLayout() {
  const navigate  = useNavigate();
  const location  = useLocation();

  async function handleLogout() {
    await logout();
    navigate("/", { replace: true });
  }

  const pageLabel = BREADCRUMBS[location.pathname] || "Admin";

  return (
    <>
    <div className="prv-wallpaper"/>
    <div className="prv-blob3"/>
    <div className="prv-app">
      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      <aside className="prv-sidebar">
        <div className="prv-logo" title="Euro Privilege" onClick={() => navigate("/admin/dashboard")}>
          P
        </div>
        <div className="prv-divider" />

        <nav className="prv-nav">
          {ITEMS.map(({ to, icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-tip={label}
              className={({ isActive }) => `prv-nav-btn${isActive ? " active" : ""}`}
            >
              <Icon name={icon} size={19} />
            </NavLink>
          ))}
        </nav>

        <div className="prv-spacer" />

        <button
          className="prv-nav-btn"
          data-tip="Sair"
          onClick={handleLogout}
          title="Sair"
        >
          <Icon name="logout" size={19} />
        </button>
      </aside>

      {/* ── Main ────────────────────────────────────────────────────── */}
      <div className="prv-main">
        {/* Topbar */}
        <header className="prv-topbar">
          <div className="prv-topbar-left">
            <span className="prv-topbar-title">Euro Privilege</span>
            <span className="prv-topbar-sep">/</span>
            <span className="prv-topbar-sub">{pageLabel}</span>
          </div>
          <div className="prv-topbar-right">
            <button className="prv-btn-logout" onClick={handleLogout}>
              Sair
            </button>
          </div>
        </header>

        {/* Page content */}
        <div className="prv-content">
          <Outlet />
        </div>
      </div>
    </div>
    </>
  );
}
